import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import InfoTooltip from "@/components/ui/infoTooltip";
import { CourseRun } from "@/common/courseRun/types";
import { ProcessedCourse } from "@/common/course/types";
import getCoursePrice from "@/lib/course/utils/getCoursePrice";
import getCourseRunExpirationDateString from "@/lib/course/utils/getCourseRunExpirationDateString";
import { formatPrice } from "@/utils/functions/formatting";
import EnrollLink from "../CourseEnroll/CourseEnrollButtons/EnrollLink";
import AuditLink from "../CourseEnroll/CourseEnrollButtons/AuditLink";
import useCourseEnrollment from "../CourseEnroll/hooks/useCourseEnrollment";
import CourseEnrollButton from "../CourseEnroll/CourseEnrollButtons";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";
import { useCurrency } from "@/contexts/UserPreferencesContext/hooks/useCurrency";
import useTranslations from "@/i18n/useTranslations";

export interface NewWaysToTakeCourse {
  activeCourseRun: CourseRun;
  course: ProcessedCourse;
  layout?: "side-by-side" | "stacked";
}

export default function WaysToTakeCourse({
  activeCourseRun,
  course,
  layout = "side-by-side",
}: NewWaysToTakeCourse) {
  const t = useTranslations("Course");
  const { isUserEnrolledInRun } =
    useCourseEnrollment({
      courseRun: activeCourseRun,
      course,
    }) || {};

  const expirationDate = getCourseRunExpirationDateString(activeCourseRun);
  const showAuditModeExpirationDate = true; //TO DO: Get from context, look into ShowAuditModeExpirationDateContext in prospectus
  const coursePrice = getCoursePrice(activeCourseRun);
  const { currencySelector } = useSingleCourseTrafficConfig({ course });
  const { getConvertedProductPrice } = useCurrency();

  const { convertedPrice, displayCurrency } = useMemo(() => {
    return getConvertedProductPrice(coursePrice, currencySelector);
  }, [coursePrice, currencySelector, getConvertedProductPrice]);

  const listItems = [
    t("worldClassInstitutions"),
    t("edxSupport"),
    t("gradedAssessments"),
    t("earnACertificate"),
  ];

  return (
    <div className="flex flex-col gap-6">
      <h2 className="m-0 md:text-3xl">{t("waysToTakeThisCourse")}</h2>
      <div
        className={`grid ${layout === "stacked" ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"} gap-6`}
      >
        <div className="border border-putty-400 rounded-xl">
          <div className="flex justify-between items-end bg-primary-500 p-6 md:py-6 md:px-10 rounded-t-xl">
            <div className="flex items-center space-x-2">
              <h3 className="text-white text-[16px] sm:text-lg m-0">
                {t("certificate")}
              </h3>
              <div className="border border-[#A5B5B1] rounded text-white px-2 py-1 text-xs">
                {t("premium")}
              </div>
            </div>
            {convertedPrice && (
              <h3 className="text-white text-[16px] sm:text-lg m-0">
                {formatPrice(convertedPrice, displayCurrency)}
              </h3>
            )}
          </div>
          <div className="flex flex-col py-10 px-6 sm:px-10 bg-white rounded-b-xl h-auto md:h-[396px] gap-8 justify-between">
            <ul className="flex flex-col gap-6 text-gray-800">
              <li>
                {t("unlimitedAccess")}{" "}
                <InfoTooltip
                  text={t("unlimitedAccessTooltip")}
                  label={t("moreInformationAbout", {
                    option: t("unlimitedAccess"),
                  })}
                  iconSvgProps={{ width: "20px", height: "20px" }}
                />
              </li>
              {listItems.map((text) => (
                <li key={text}>{text}</li>
              ))}
            </ul>
            <CourseEnrollButton
              buttonClass="w-full"
              courseRun={activeCourseRun}
              course={course}
              location="cert-ways-to-take-course"
              price={coursePrice}
            />
          </div>
        </div>
        <div className="border border-putty-400 rounded-xl">
          <div className="flex p-6 md:py-6 md:px-10 bg-putty-400 rounded-t-xl">
            <h3 className="text-primary-500 text-[16px] sm:text-lg m-0">
              {t("audit")}
            </h3>
          </div>
          <div className="flex flex-col py-10 px-6 sm:px-10 bg-white rounded-b-xl h-auto md:h-[396px] gap-8 justify-between">
            <ul className="flex flex-col gap-6 text-gray-800">
              {showAuditModeExpirationDate && expirationDate && (
                <li>
                  {t("accessExpiresDate", { date: expirationDate })}{" "}
                  <InfoTooltip
                    text={t("limitedAccessTooltip")}
                    label={t("moreInformationAbout", {
                      option: t("limitedAccess"),
                    })}
                    iconSvgProps={{ width: "20px", height: "20px" }}
                  />
                </li>
              )}
              {listItems.slice(0, 2).map((text) => (
                <li key={text}>{text}</li>
              ))}
            </ul>
            <AuditLink courseRun={activeCourseRun} course={course} />
          </div>
        </div>
      </div>
    </div>
  );
}
